import React, { useEffect, useRef, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import { motion } from "framer-motion";
import { ColDef } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useNavigate } from "react-router-dom";
import { CircularProgress, IconButton, Menu, MenuItem } from "@mui/material";
import MultiLineZoomChart from "../../../components/charts/MultiLineZoomChart";
import axiosInterceptorInstance from "../../../utils/axios.interceptor";
import Calendar from "../../../components/calendar/Calender";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "ag-grid-community/styles/ag-grid.css";

const InternationalTopup = () => {
  const [rowData, setRowData] = useState<any[] | null>(null);
  const [processedData, setProcessedData] = useState<any[]>([]);
  const [totalTransactionData, setTotalTransactionData] = useState<any[]>([]);
  const [totalBuyingData, setTotalBuyingData] = useState<any[]>([]);
  const agGridRef = useRef<AgGridReact | null>(null);
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs | null>(
    dayjs().add(-7, "days"),
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs | null>(dayjs());
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [calendarOpen, setCalendarOpen] = useState(false);
  const navigate = useNavigate();

  const controller = new AbortController();

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (localStorage.getItem("user")) {
      var user = JSON.parse(localStorage.getItem("user") || "");
      if (
        user.Email == "tahir.shahzad@now.net.pk" ||
        user.Email == "rameez.hassan@now.net.pk" ||
        user.Email == "azaz.manzoor@now.net.pk" ||
        user.Email == "marketing@crm-talkhome.com"
      ) {
        navigate("/tha/international-topup");
      } else {
        navigate("/support/portin");
      }
    }
  }, []);

  useEffect(() => {
    if (selectedStartDate && selectedEndDate) {
      const startDateParam = selectedStartDate.format("YYYY-MM-DD");
      const endDateParam = selectedEndDate.add(1, "days").format("YYYY-MM-DD");
      setRowData(null);
      fetchDataFromAPI(startDateParam || "", endDateParam || "");
    }
  }, [selectedStartDate, selectedEndDate]);

  const fetchDataFromAPI = async (startDate: string, endDate: string) => {
    await axiosInterceptorInstance
      .get(
        `ThaCrm/internationalTopupReport?StartDate=${startDate}&EndDate=${endDate}`,
        { signal: controller.signal },
      )
      .then((response) => {
        if (response.data.data) {
          const formattedData = response.data.data.map((item: any) => ({
            ...item,
            PaymentDate: item.PaymentDate.split("T")[0],
          }));
          setRowData(formattedData);
        } else {
          console.error("No Record Found");
        }
      })
      .catch((error) => {
        console.error(error);
      });
  };

  useEffect(() => {
    const transformData = () => {
      if (!rowData || !Array.isArray(rowData)) {
        return { processed: [], totalTransaction: [], totalBuying: [] };
      }
      const allCurrencies = new Set<string>();
      const result = rowData.reduce(
        (acc, item) => {
          const date = item.PaymentDate.split("T")[0];
          const origin = item.Orignation;
          const destination = item.Destination;
          const totalTransaction = item.Total_Transaction;
          const sellingPrice = item.Selling_Amount;
          const currency = item.Currency;
          allCurrencies.add(currency);
          // Initialize processedData
          if (!acc.processed[date]) {
            acc.processed[date] = { Date: date };
          }

          if (!acc.processed[date][origin]) {
            acc.processed[date][origin] = { total: 0 };
          }

          if (acc.processed[date][origin][destination]) {
            acc.processed[date][origin][destination] += totalTransaction;
          } else {
            acc.processed[date][origin][destination] = totalTransaction;
          }
          acc.processed[date][origin].total += totalTransaction;

          if (!acc.totalTransaction[date]) {
            acc.totalTransaction[date] = { Date: date };
          }
          if (!acc.totalTransaction[date][currency]) {
            acc.totalTransaction[date][currency] = 0;
          }
          acc.totalTransaction[date][currency] += totalTransaction;

          if (!acc.totalBuying[date]) {
            acc.totalBuying[date] = { Date: date };
          }
          if (!acc.totalBuying[date][currency]) {
            acc.totalBuying[date][currency] = 0;
          }
          acc.totalBuying[date][currency] += sellingPrice;

          return acc;
        },
        { processed: {}, totalTransaction: {}, totalBuying: {} },
      );

      const allOrigins = new Set();
      for (const date in result.processed) {
        for (const origin in result.processed[date]) {
          if (origin !== "Date") {
            allOrigins.add(origin);
          }
        }
      }

      for (const date in result.processed) {
        allOrigins.forEach((origin: any) => {
          if (!result.processed[date][origin]) {
            result.processed[date][origin] = { total: 0 };
          }
        });
      }

      const formattedTotalTransaction = Object.values(
        result.totalTransaction,
      ).map((item: any) => ({
        ...(item || {}),
        ...Array.from(allCurrencies).reduce((acc: any, cur: any) => {
          acc[cur] = item[cur] || 0;
          return acc;
        }, {}),
      }));

      const formattedTotalBuying = Object.values(result.totalBuying).map(
        (item: any) => ({
          ...(item || {}),
          ...Array.from(allCurrencies).reduce((acc: any, cur: any) => {
            acc[cur] = parseFloat((item[cur] || 0).toFixed(2));
            return acc;
          }, {}),
        }),
      );
      return {
        processed: Object.values(result.processed),
        totalTransaction: formattedTotalTransaction.reverse(),
        totalBuying: formattedTotalBuying.reverse(),
      };
    };

    const { processed, totalTransaction, totalBuying } = transformData();
    setProcessedData(processed);
    setTotalTransactionData(totalTransaction);
    setTotalBuyingData(totalBuying);
  }, [rowData]);

  console.log(processedData);
  console.log(totalTransactionData);
  console.log(totalBuyingData);

  const columnDefs: ColDef[] = [
    {
      field: "PaymentDate",
      headerName: "Date",
      filter: true,
      sortable: true,
      resizable: true,
      sort: "desc",
    },
    {
      field: "Orignation",
      headerName: "Orignation",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Destination",
      headerName: "Destination",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Denomination",
      headerName: "Denomination",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Platform",
      headerName: "Platform",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "ProductSubCategory",
      headerName: "ProductSubCategory",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Currency",
      headerName: "Currency",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Total_Transaction",
      headerName: "TotalTransaction",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "BuyingPrice",
      headerName: "BuyingPrice",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Margin",
      headerName: "Margin",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Selling_Amount",
      headerName: "SellingAmount",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "Profit",
      headerName: "Profit",
      filter: true,
      sortable: true,
      resizable: true,
    },
    {
      field: "TransactionCost",
      headerName: "TransactionCost",
      filter: true,
      sortable: true,
      resizable: true,
    },
  ];

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const toggleCalendar = () => {
    setCalendarOpen(!calendarOpen);
    handleMenuClose();
  };

  const exportGridDataAsCSV = () => {
    agGridRef.current?.api.exportDataAsCsv({
      fileName: "international-topup-report.csv",
    });
  };

  const handleStartDateChange = (date: Dayjs | null) => {
    if (date !== null && date.isValid()) {
      setSelectedStartDate(date);
    }
  };

  const handleEndDateChange = (date: Dayjs | null) => {
    if (date !== null && date.isValid()) {
      setSelectedEndDate(date);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ ease: "linear", duration: 0.5 }}
    >
      <div>
        <h2 className="heading">International Topup Report</h2>
        {selectedEndDate &&
          selectedStartDate &&
          Math.floor(selectedEndDate.diff(selectedStartDate, "day", true)) ===
            7 && <span className="small-text">Last 7 Days</span>}
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "-35px",
          position: "relative",
          top: "-20px",
        }}
      >
        <div></div>
        <IconButton
          onClick={(e: any) => {
            handleMenuClick(e);
          }}
        >
          <MoreVertIcon />
        </IconButton>
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={toggleCalendar}></MenuItem>
          {selectedStartDate && selectedEndDate && (
            <Calendar
              selectedStartDate={selectedStartDate}
              selectedEndDate={selectedEndDate}
              onStartDateChange={handleStartDateChange}
              onEndDateChange={handleEndDateChange}
            />
          )}
        </Menu>
      </div>

      {!rowData && (
        <div className="dummy-conatiner">
          <CircularProgress size={40} />
        </div>
      )}

      {processedData?.length > 0 && (
        <div className="boxShadow" style={{ marginBottom: "10px" }}>
          <div style={{ padding: "40px" }}>
            <MultiLineZoomChart nestedData={processedData} />

            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                gap: "10px",
              }}
            >
              <div style={{ flex: "1" }}>
                <h2>Total Transaction Chart</h2>
                <MultiLineZoomChart chartData={totalTransactionData} />
              </div>
              <div style={{ flex: "1" }}>
                <h2>Total Revenue Chart</h2>
                <MultiLineZoomChart chartData={totalBuyingData} />
              </div>
            </div>
          </div>
        </div>
      )}

      {rowData && (
        <>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginBottom: "5px",
            }}
          >
            <button
              className="small-btn"
              style={{ visibility: rowData.length > 0 ? "visible" : "hidden" }}
              onClick={exportGridDataAsCSV}
            >
              Export as CSV
            </button>
          </div>
          <div
            className="ag-theme-alpine"
            style={{ height: "425px", overflowY: "auto" }}
          >
            <AgGridReact
              ref={agGridRef}
              paginationPageSize={50}
              pagination={true}
              rowData={rowData}
              columnDefs={columnDefs}
            ></AgGridReact>
          </div>
        </>
      )}
    </motion.div>
  );
};

export default InternationalTopup;
